import * as React from 'react';
import { ReactNode, RefObject, useContext, useEffect, useRef } from 'react';
import { LabelledPersonSuggester } from '../../../components/form/LabelledPersonSuggester';
import { PersonApi, ReferencedPersonNameDTO } from 'api/core';
import { LabelledDropdown } from '../../../components/form/LabelledDropdown';
import { ValidationResults } from '../../../../util/validation/interfaces';
import { CheckPermissionFunction } from '../../../../Auth/components/PermissionGate';
import { LinkedPersonName } from './LinkedPersonName';
import { SelectedPerson, selectedPersonToWorkPerson, workPersonToSelectedPerson } from '../suggester/PersonSuggester';
import { getAuthMiddleware } from '../../../../Auth/utils/GetAuthMiddleware';
import { AuthContext } from '../../../../Auth/components/AuthProvider';
import { SelectItem } from 'primereact/selectitem';
import { Internal } from '../LinkedPerson';

interface Props {
  person: Internal;
  validation: ValidationResults;
  checkPermission: CheckPermissionFunction;
  onChange: (person: Internal) => void;
  autoFocus: boolean;
  roleOptions: SelectItem[];
}

export const LinkedPersonIntern = (props: Props): ReactNode => {
  const {actions} = useContext(AuthContext);
  const api: PersonApi = new PersonApi().withMiddleware(getAuthMiddleware(actions));
  const [options, setOptions] = React.useState<ReferencedPersonNameDTO[]>(undefined);
  const [loadedPersonNamesForPersonId, setLoadedPersonNamesForPersonId] = React.useState<number>();
  const inputRef: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);

  useEffect((): void => {
    setOptions(props?.person?.personName ? [props.person.personName] : []);
    setLoadedPersonNamesForPersonId(undefined);
    if (props.person.personName?.id === 0) {
      void loadNames(props.person.personName.personId);
    }
  }, [props.person.personName?.personId]);

  const loadNames = async (personId?: number): Promise<void> => {
    if (!personId || loadedPersonNamesForPersonId === personId) return;
    const rpns: ReferencedPersonNameDTO[] = await api.getPersonNames({personId});
    setOptions(rpns);
    setLoadedPersonNamesForPersonId(personId);
    if (rpns.length === 0) return;
    if (rpns.some((rpn: ReferencedPersonNameDTO): boolean => rpn.id === props.person.personName.id)) {
      return;
    }
    const selected: ReferencedPersonNameDTO = rpns.find((rpn: ReferencedPersonNameDTO): boolean => rpn.isMain) || rpns[0];
    selectName(selected);
  };

  const selectName = (personName?: ReferencedPersonNameDTO): void => {
    if (!personName) return;
    props.onChange({
      ...props.person,
      personName: {
        ...props.person.personName,
        givenname: personName.givenname,
        id: personName.id,
        surname: personName.surname,
      },
    });
  };

  if(props.autoFocus && inputRef.current) {
    inputRef.current.focus();
  }

  return <>
    <LabelledPersonSuggester
      value={props?.person?.personName ? [workPersonToSelectedPerson(props.person)] : []}
      autoFocus={props.autoFocus}
      multiple={false}
      ref={inputRef}
      id={`workPerson-${props.person.sequence}-person`}
      permissions={{canRead: true, canWrite: true, required: true}}
      validation={props.validation.fields?.person_name || {valid: false}}
      onChange={(refs: SelectedPerson[]): void => {
        if (refs.length > 0) {
          props.onChange({
            ...props.person,
            ...selectedPersonToWorkPerson(refs[0])
          });
        } else {
          props.onChange({
            ...props.person,
            personName: undefined,
            externalIds: undefined,
            topOrgas: undefined,
          });
        }
      }}
      label={'work.attr.person.attr.person'}
      wrapperClassName={'flex-1 min-w-0'}
    />
    <LinkedPersonName
      options={options}
      value={props.person?.personName?.id}
      id={`workPerson-${props.person.sequence}-personName`}
      disabled={!options || options.length === 0}
      permissions={{canRead: true, canWrite: true, required: true}}
      validation={props?.validation?.fields?.person_name?.child?.fields?.id || {valid: false}}
      onChange={(id: number): void => selectName(options.find((option: ReferencedPersonNameDTO): boolean => option.id === id))}
      onShow={(): void => void loadNames(props.person?.personName?.personId)}
      label={'work.attr.person.attr.nameVariant'}
      wrapperClassName={'flex-1 min-w-0'}
    />
    <LabelledDropdown
      className={'w-full border-noround-top'}
      wrapperClassName={'flex-1 min-w-0'}
      value={props.person.role}
      id={`workPerson-${props.person.sequence}-role`}
      permissions={props.checkPermission('role')}
      validation={props.validation.fields?.role}
      onChange={(role: string): void => props.onChange({...props.person, role})}
      label={'work.attr.person.attr.role'}
      options={props.roleOptions}/>
  </>;
};
